import type { TextProps } from "@chakra-ui/layout";
import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  type AccountMoveFragment,
  AddDocumentsToAccountMoveDocument,
  type AddDocumentsToAccountMoveMutation,
  type AddDocumentsToAccountMoveMutationVariables,
  BankDocumentTypeEnum,
  ExpenseOrderByColumnEnum,
  ExpensesForPaymentMatchDocument,
  type ExpensesForPaymentMatchQuery,
  type ExpensesForPaymentMatchQueryVariables,
  ExpensesWhereColumn,
  InvoicesForPaymentMatchDocument,
  type InvoicesForPaymentMatchQuery,
  type InvoicesForPaymentMatchQueryVariables,
  MatchPaymentModalFilterOptionsDocument,
  type MatchPaymentModalFilterOptionsQuery,
  type MatchPaymentModalFilterOptionsQueryVariables,
  OiProformaWhereColumn,
  OiWhereColumn,
  OutgoingInvoiceOrderByColumnEnum,
  OutgoingProformaInvoiceOrderByColumnEnum,
  ProformasForPaymentMatchDocument,
  type ProformasForPaymentMatchQuery,
  type ProformasForPaymentMatchQueryVariables,
  SortOrder,
} from "@src/__generated__/graphql";
import { BankStore } from "@src/components/modules/bank/stores";
import type { BodyColumnDef } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import { EXCHANGE_RATE_DENOMINATION } from "@src/stores/forms/SettingsModalState/FinancialSettingsState";
import type { FiltersPanelSearchRef } from "@src/utils/components/filters/components/filters-panel-search";
import { currency, denominate } from "@src/utils/formatters";
import mapToOptions from "@src/utils/map-to-options";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { action, computed, makeObservable, observable } from "mobx";
import { createRef } from "react";
import { SelectedDocument } from "../models/selected-document";
import {
  createExpenseColumnsArgs,
  createExpenseFiltersArgs,
} from "./expense-table-state-init-args";
import {
  createInvoiceColumnsArgs,
  createInvoiceFiltersArgs,
} from "./invoice-table-state-init-args";
import {
  createProformaColumnsArgs,
  createProformaFiltersArgs,
} from "./proforma-table-state-init-args";
import {
  type MatchPaymentTableItem,
  MatchPaymentTableState,
} from "./table-state";

type ExpenseForPaymentItem = NonNullable<
  ExpensesForPaymentMatchQuery["expenses"]
>["data"][0];
type InvoiceForPaymentItem = NonNullable<
  InvoicesForPaymentMatchQuery["outgoingInvoices"]
>["data"][0];
type ProformaForPaymentItem = NonNullable<
  ProformasForPaymentMatchQuery["outgoingProformaInvoices"]
>["data"][0];

type BankMatchPaymentModalDisclosureData = {
  accountMove: AccountMoveFragment;
  onConfirm: () => void;
};
export class BankMatchPaymentModalStore implements BaseStore {
  readonly tableKey = "bank-detail";
  @observable.ref
  columns: BodyColumnDef<AccountMoveFragment, unknown>[] = [];
  disclosure = new DisclosureState<BankMatchPaymentModalDisclosureData>({
    onOpen: (additionalData) => {
      this.setSelectedTab(
        // NOTE: If amount is positive we want to pair it to invoices, otherwise to expenses
        // NOTE: Tabs: 0 - Expenses, 1 - Invoices, 2 - Proformas
        Math.sign(additionalData?.accountMove.amount ?? 0) > 0 ? 1 : 0,
      );
      this.setColumns(BankStore.getColumnsWithoutActions(true));
      this.selectedDocuments = [];
      this.expensesTableState.reset(
        true,
        [ExpensesWhereColumn.Status, ExpensesWhereColumn.PaymentStatus],
        true,
        false,
      );
      this.invoicesTableState.reset(
        true,
        [OiWhereColumn.PaymentStatus],
        true,
        false,
      );
      this.proformasTableState.reset(
        true,
        [OiProformaWhereColumn.PaymentStatus],
        true,
        false,
      );
      this.fetchFilterOptions();
      this.fetchExpenseData();
      this.fetchInvoiceData();
      this.fetchProformaData();

      if (!additionalData?.accountMove.documents) return;
      console.log(additionalData.accountMove.documents);
      const selectedIds = {
        [BankDocumentTypeEnum.Expense]: [] as string[],
        [BankDocumentTypeEnum.OutgoingInvoice]: [] as string[],
        [BankDocumentTypeEnum.OutgoingProformaInvoice]: [] as string[],
      };
      const documents = {
        [BankDocumentTypeEnum.Expense]: [] as MatchPaymentTableItem[],
        [BankDocumentTypeEnum.OutgoingInvoice]: [] as MatchPaymentTableItem[],
        [BankDocumentTypeEnum.OutgoingProformaInvoice]:
          [] as MatchPaymentTableItem[],
      };

      for (const document of additionalData.accountMove.documents) {
        selectedIds[document.document_type].push(document.document_id);
        documents[document.document_type].push({
          id: document.document_id,
          amount: document.amount.toString(),
          currency: document.document_currency,
          exchange_rate: document.exchange_rate,
          documentNo: document.document_no,
        });
      }

      this.expensesTableState.setSelectedTableItems(
        documents[BankDocumentTypeEnum.Expense],
      );
      this.expensesTableState.handleSelectRow(
        selectedIds[BankDocumentTypeEnum.Expense],
      );
      this.invoicesTableState.setSelectedTableItems(
        documents[BankDocumentTypeEnum.OutgoingInvoice],
      );
      this.invoicesTableState.handleSelectRow(
        selectedIds[BankDocumentTypeEnum.OutgoingInvoice],
      );
      this.proformasTableState.setSelectedTableItems(
        documents[BankDocumentTypeEnum.OutgoingProformaInvoice],
      );
      this.proformasTableState.handleSelectRow(
        selectedIds[BankDocumentTypeEnum.OutgoingProformaInvoice],
      );
    },
    onClose: () => {
      this.setSelectedTab(0);
    },
  });

  filterOptionsFetcher = new FetchHelper<
    MatchPaymentModalFilterOptionsQuery,
    MatchPaymentModalFilterOptionsQueryVariables
  >(MatchPaymentModalFilterOptionsDocument);

  expensesTableState: MatchPaymentTableState<
    ExpensesForPaymentMatchQuery,
    ExpensesForPaymentMatchQueryVariables,
    ExpensesWhereColumn,
    ExpenseOrderByColumnEnum,
    ExpenseForPaymentItem & MatchPaymentTableItem
  >;

  invoicesTableState: MatchPaymentTableState<
    InvoicesForPaymentMatchQuery,
    InvoicesForPaymentMatchQueryVariables,
    OiWhereColumn,
    OutgoingInvoiceOrderByColumnEnum,
    InvoiceForPaymentItem & MatchPaymentTableItem
  >;

  proformasTableState: MatchPaymentTableState<
    ProformasForPaymentMatchQuery,
    ProformasForPaymentMatchQueryVariables,
    OiProformaWhereColumn,
    OutgoingProformaInvoiceOrderByColumnEnum,
    ProformaForPaymentItem & MatchPaymentTableItem
  >;

  @observable
  selectedDocuments: SelectedDocument[] = [];
  mutator = new MutationHelper<
    AddDocumentsToAccountMoveMutation,
    AddDocumentsToAccountMoveMutationVariables
  >(AddDocumentsToAccountMoveDocument);

  @observable.ref
  selectedTab = 0;

  @observable.ref
  searchInputRef = createRef<FiltersPanelSearchRef>();

  constructor(public appStore: AppStore) {
    makeObservable(this);
    this.expensesTableState = new MatchPaymentTableState(
      `${this.tableKey}-expenses-table`,
      ExpensesForPaymentMatchDocument,
      createExpenseFiltersArgs(),
      [
        {
          column: ExpenseOrderByColumnEnum.Id,
          order: SortOrder.Desc,
        },
      ],
      createExpenseColumnsArgs({
        selectedRows: () => this.expensesTableState.selectedIds,
        onMatchClick: (selectedIds) => {
          this.expensesTableState.handleSelectRow(selectedIds);
        },
      }),
      this.fetchExpenseData,
      (selectedIds, selectedTableItems) =>
        this.handleSelectDocument(
          BankDocumentTypeEnum.Expense,
          selectedIds,
          selectedTableItems,
        ),
    );

    this.invoicesTableState = new MatchPaymentTableState(
      `${this.tableKey}-invoices-table`,
      InvoicesForPaymentMatchDocument,
      createInvoiceFiltersArgs(),
      [
        {
          column: OutgoingInvoiceOrderByColumnEnum.InvoiceNo,
          order: SortOrder.Desc,
        },
      ],
      createInvoiceColumnsArgs({
        selectedRows: () => this.invoicesTableState.selectedIds,
        onMatchClick: (selectedIds) => {
          this.invoicesTableState.handleSelectRow(selectedIds);
        },
      }),
      this.fetchInvoiceData,
      (selectedIds, selectedTableItems) =>
        this.handleSelectDocument(
          BankDocumentTypeEnum.OutgoingInvoice,
          selectedIds,
          selectedTableItems,
        ),
    );

    this.proformasTableState = new MatchPaymentTableState(
      `${this.tableKey}-proformas-table`,
      ProformasForPaymentMatchDocument,
      createProformaFiltersArgs(),
      [
        {
          column: OutgoingProformaInvoiceOrderByColumnEnum.InvoiceNo,
          order: SortOrder.Desc,
        },
      ],
      createProformaColumnsArgs({
        selectedRows: () => this.proformasTableState.selectedIds,
        onMatchClick: (selectedIds) => {
          this.proformasTableState.handleSelectRow(selectedIds);
        },
      }),
      this.fetchProformaData,
      (selectedIds, selectedTableItems) =>
        this.handleSelectDocument(
          BankDocumentTypeEnum.OutgoingProformaInvoice,
          selectedIds,
          selectedTableItems,
        ),
    );
  }

  @action.bound
  setColumns(columns: BodyColumnDef<AccountMoveFragment, unknown>[]) {
    this.columns = columns;
  }

  @action.bound
  setSelectedTab(tabIndex: number) {
    this.selectedTab = tabIndex;
    const tableStateProps = this.getTableStateProps(tabIndex);
    this.searchInputRef.current?.setValue(tableStateProps.searchTerm);
  }

  @action.bound
  private getTableStateProps(
    tabIndex: number,
  ): (typeof MatchPaymentTableState)["prototype"]["tableProps"] {
    switch (tabIndex) {
      case 0:
        return this.expensesTableState.tableProps;
      case 1:
        return this.invoicesTableState.tableProps;
      case 2:
        return this.proformasTableState.tableProps;
      default:
        return this.expensesTableState.tableProps;
    }
  }

  @computed
  get selectedTableStateProps() {
    return this.getTableStateProps(this.selectedTab);
  }

  @computed
  get selectedDocumentsIncludeForeignCurrency(): boolean {
    if (!this.selectedDocuments.length) return false;
    return this.selectedDocuments.some(
      (document) => document.hasForeignCurrency,
    );
  }

  @computed
  get selectedAccountMove(): AccountMoveFragment | undefined {
    return this.disclosure.additionalData?.accountMove;
  }

  @computed
  get selectedDocumentsTotalAmount(): number {
    return this.selectedDocuments.reduce(
      (prev, { denominatedAmountInMoveCurrency }) =>
        prev + denominatedAmountInMoveCurrency,
      0,
    );
  }

  @computed
  get isSelectedMoveAmountPositive(): boolean {
    return (this.selectedAccountMove?.amount ?? 0) >= 0;
  }

  @computed
  get isBalanceNegative(): boolean {
    return this.isSelectedMoveAmountPositive
      ? Math.sign(this.balance) < 0
      : Math.sign(this.balance) > 0;
  }

  @computed
  get balance(): number {
    if (!this.selectedAccountMove) return 0;
    if (this.isSelectedMoveAmountPositive) {
      return (
        this.selectedAccountMove.amount - this.selectedDocumentsTotalAmount
      );
    }

    return (
      this.selectedAccountMove.amount +
      Math.abs(this.selectedDocumentsTotalAmount)
    );
  }

  @computed
  get balanceFormatted(): string | null {
    if (!this.selectedAccountMove) return null;
    return currency.formatByCurrency(
      Math.abs(this.balance),
      this.selectedAccountMove.currency,
    );
  }

  @computed
  get balanceColor(): TextProps["color"] {
    if (this.balance === 0) return "green.400";
    return this.isBalanceNegative ? "red.500" : undefined;
  }

  @computed
  get isConfirmDisabled(): boolean {
    return (
      this.isBalanceNegative ||
      this.selectedDocuments.some((document) => document.isEditEnabled.value)
    );
  }

  @action.bound
  async fetchExpenseData() {
    const [data, error] = await this.expensesTableState.fetcher.fetch(
      this.expensesTableState.fetchVariables,
    );

    if (error) return;
    if (!data.expenses) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong while fetching data, please try again later.`,
      });
      return;
    }

    this.expensesTableState.setTableData(
      data.expenses.data.map((item) => ({
        ...item,
        amount: item.stats.total_amount_with_vat_in_currency.toString(),
        currency: item.currency,
        documentNo: item.expense_no ?? "",
        exchange_rate: item.exchange_rate,
      })),
    );
    this.expensesTableState.pagination.setFromPaginatorInfo(
      data.expenses.paginatorInfo,
    );
  }

  @action.bound
  async fetchInvoiceData() {
    const [data, error] = await this.invoicesTableState.fetcher.fetch(
      this.invoicesTableState.fetchVariables,
    );

    if (error) return;
    if (!data.outgoingInvoices) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong while fetching data, please try again later.`,
      });
      return;
    }

    this.invoicesTableState.setTableData(
      data.outgoingInvoices.data.map((item) => ({
        ...item,
        amount: item.stats.billing_amount_with_vat_in_currency.toString(),
        currency: item.currency,
        documentNo: item.invoice_no,
        exchange_rate: item.exchange_rate,
      })),
    );
    this.invoicesTableState.pagination.setFromPaginatorInfo(
      data.outgoingInvoices.paginatorInfo,
    );
  }

  @action.bound
  async fetchProformaData() {
    const [data, error] = await this.proformasTableState.fetcher.fetch(
      this.proformasTableState.fetchVariables,
    );

    if (error) return;
    if (!data.outgoingProformaInvoices) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong while fetching data, please try again later.`,
      });
      return;
    }

    this.proformasTableState.setTableData(
      data.outgoingProformaInvoices.data.map((item) => ({
        ...item,
        amount: item.stats.total_amount_with_vat_in_currency.toString(),
        currency: item.currency,
        documentNo: item.invoice_no,
        exchange_rate: item.exchange_rate,
      })),
    );
    this.proformasTableState.pagination.setFromPaginatorInfo(
      data.outgoingProformaInvoices.paginatorInfo,
    );
  }

  @action.bound
  async fetchFilterOptions() {
    this.expensesTableState.where.filtersByColumn
      .get(ExpensesWhereColumn.WorkspaceCompanyId)
      ?.setOptions(
        mapToOptions.workspaceCompanies(this.appStore.workspaceStore.companies),
      );

    const bankAcocuntOptions = mapToOptions.companiesToBankAccountOptions(
      this.appStore.workspaceStore.companies,
    );

    this.expensesTableState.where.filtersByColumn
      .get(ExpensesWhereColumn.WorkspaceBankAccountId)
      ?.setOptions(bankAcocuntOptions);
    this.invoicesTableState.where.filtersByColumn
      .get(OiWhereColumn.WorkspaceBankAccountId)
      ?.setOptions(bankAcocuntOptions);
    this.proformasTableState.where.filtersByColumn
      .get(OiProformaWhereColumn.WorkspaceBankAccountId)
      ?.setOptions(bankAcocuntOptions);

    const [data, error] = await this.filterOptionsFetcher.fetch();

    if (!data || error) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong with fetching filters, please try again.`,
      });
      return;
    }

    const brandOptions = mapToOptions.clients.toOptions(data.clientsSimpleMap);
    this.expensesTableState.where.filtersByColumn
      .get(ExpensesWhereColumn.BrandId)
      ?.setOptions(brandOptions);
    this.invoicesTableState.where.filtersByColumn
      .get(OiWhereColumn.BrandId)
      ?.setOptions(brandOptions);
    this.invoicesTableState.where.filtersByColumn
      .get(OiWhereColumn.OwnerId)
      ?.setOptions(
        mapToOptions.projectManagers.toOptions(data.projectManagerSimpleMap),
      );
    this.proformasTableState.where.filtersByColumn
      .get(OiProformaWhereColumn.BrandId)
      ?.setOptions(brandOptions);
    this.proformasTableState.where.filtersByColumn
      .get(OiProformaWhereColumn.OwnerId)
      ?.setOptions(
        mapToOptions.projectManagers.toOptions(data.projectManagerSimpleMap),
      );
  }

  @action.bound
  private handleSelectDocument(
    documentType: BankDocumentTypeEnum,
    selectedIds: string[],
    selectedTableItems: Array<MatchPaymentTableItem>,
  ) {
    this.removeNonSelectedDocuments(selectedIds, documentType);
    this.addNewSelectedDocument(selectedTableItems, documentType);
  }

  @action.bound
  private removeNonSelectedDocuments(
    selectedIds: string[],
    documentType: BankDocumentTypeEnum,
  ) {
    const idsToRemove: string[] = [];
    const documentCount = this.selectedDocuments.length;

    for (let i = 0; i < documentCount; i++) {
      const { $ } = this.selectedDocuments[i];
      if ($.documentType.$ !== documentType) continue;
      if (selectedIds.includes($.documentId.$)) continue;
      idsToRemove.push($.documentId.$);
    }

    for (const id of idsToRemove) {
      const index = this.selectedDocuments.findIndex(
        ({ $ }) => $.documentId.$ === id,
      );
      if (index === -1) continue;

      this.selectedDocuments.splice(index, 1);
    }
  }

  @action.bound
  private addNewSelectedDocument(
    selectedTableItems: Array<MatchPaymentTableItem>,
    documentType: BankDocumentTypeEnum,
  ) {
    for (const item of selectedTableItems) {
      const document = this.selectedDocuments.find(
        ({ $ }) =>
          $.documentType.$ === documentType && $.documentId.$ === item.id,
      );

      if (document) continue;
      const defaultExchangeRate = denominate(
        1,
        EXCHANGE_RATE_DENOMINATION,
      ).toString();

      this.selectedDocuments.push(
        new SelectedDocument(
          {
            documentType: documentType,
            documentId: item.id,
            date: new Date(),
            amount:
              documentType === BankDocumentTypeEnum.Expense
                ? (Math.abs(Number(item.amount)) * -1).toString()
                : item.amount,
            exchangeRate:
              item.currency.id !== this.selectedAccountMove?.currency.id
                ? (item.exchange_rate?.toString() ?? defaultExchangeRate)
                : defaultExchangeRate,
          },
          item.currency,
          item.documentNo,
          this.selectedAccountMove?.currency,
        ),
      );
    }
  }

  @action.bound
  async handleConfirm() {
    if (!this.selectedAccountMove) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Something went wrong, please try again later.`,
      });

      captureEvent({
        message:
          "FE: selectedAccountMove isn't set while trying to trigger AddDocumentsToAccountMoveDocument mutation.",
      });

      return;
    }

    const [_, error] = await this.mutator.mutate({
      bankAccountMoveId: this.selectedAccountMove.id,
      documents: this.selectedDocuments.map(
        (document) => document.mutationInput,
      ),
    });

    if (error) return;

    this.disclosure.additionalData?.onConfirm();
    this.disclosure.onClose();
  }

  @action.bound
  removeDocument(document: SelectedDocument) {
    let tableState: (typeof MatchPaymentTableState)["prototype"] | null = null;

    switch (document.$.documentType.value) {
      case BankDocumentTypeEnum.Expense:
        tableState = this.expensesTableState;
        break;
      case BankDocumentTypeEnum.OutgoingInvoice:
        tableState = this.invoicesTableState;
        break;
      case BankDocumentTypeEnum.OutgoingProformaInvoice:
        tableState = this.proformasTableState;
        break;
      default:
        captureEvent({
          message: "FE: DocumentType unknown while trying to remove document.",
        });
        this.appStore.UIStore.toast({
          status: "error",
        });
        return;
    }

    tableState.handleSelectRow(
      tableState.selectedIds.filter((id) => id !== document.$.documentId.value),
    );
  }

  @computed
  private get currentTabAmountFromFilter() {
    switch (this.selectedTab) {
      case 0:
        return this.expensesTableState.where.filtersByColumn.get(
          ExpensesWhereColumn.AmountFrom,
        );
      case 1:
        return this.invoicesTableState.where.filtersByColumn.get(
          OiWhereColumn.AmountFrom,
        );
      case 2:
        return this.proformasTableState.where.filtersByColumn.get(
          OiProformaWhereColumn.AmountFrom,
        );
      default:
        return this.expensesTableState.where.filtersByColumn.get(
          ExpensesWhereColumn.AmountFrom,
        );
    }
  }

  @computed
  private get currentTabAmountToFilter() {
    switch (this.selectedTab) {
      case 0:
        return this.expensesTableState.where.filtersByColumn.get(
          ExpensesWhereColumn.AmountTo,
        );
      case 1:
        return this.invoicesTableState.where.filtersByColumn.get(
          OiWhereColumn.AmountTo,
        );
      case 2:
        return this.proformasTableState.where.filtersByColumn.get(
          OiProformaWhereColumn.AmountTo,
        );
      default:
        return this.expensesTableState.where.filtersByColumn.get(
          ExpensesWhereColumn.AmountTo,
        );
    }
  }

  @action.bound
  handleChangeAmountFrom(value: string) {
    this.currentTabAmountFromFilter?.setValue(value);
  }

  @computed
  get amountFromInputValue() {
    return this.currentTabAmountFromFilter?.value?.[0] ?? "";
  }

  @action.bound
  handleChangeAmountTo(value: string) {
    this.currentTabAmountToFilter?.setValue(value);
  }

  @computed
  get amountToInputValue() {
    return this.currentTabAmountToFilter?.value?.[0] ?? "";
  }
}
